import {IssueRelativeTime} from '@github-ui/ago'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {IssueStateIcons, PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'
import {IssueTypeToken} from '@github-ui/issue-type-token'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ListItemTrailingBadge} from '@github-ui/list-view/ListItemTrailingBadge'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {ProgressCircle} from '@github-ui/progress-circle'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {BlockedIcon, CommentIcon, GitPullRequestIcon, InfoIcon, MilestoneIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Link, Token} from '@primer/react'
import {memo, type ElementType, type KeyboardEvent} from 'react'

import {AssigneeAvatars} from '../shared/AssigneeAvatars'
import {renderLabelBadges} from '../shared/LabelBadges'
import {unreadRowClassName, UnreadRowLabel} from '../shared/UnreadRowIndicator'
import type {FilterSelectType} from '../shared/types/list-types'
import inlineCodeStyles from '../shared/TitleInlineCode.module.css'
import styles from './IssueListItem.module.css'
import type {EnrichedIssue, IssueLinkedPullRequestSummary, IssueStateReason, IssueStateType} from './types/list-types'

function getIssueStateIcon(state: IssueStateType, stateReason: IssueStateReason) {
  if (state === 'OPEN') return <IssueStateIcons.OPEN />
  if (stateReason === 'NOT_PLANNED') return <IssueStateIcons.NOT_PLANNED />
  if (stateReason === 'DUPLICATE') return <IssueStateIcons.DUPLICATE />
  return <IssueStateIcons.COMPLETED />
}

function getLinkedPullRequestVisual(pullRequest: IssueLinkedPullRequestSummary) {
  switch (pullRequest.state?.toUpperCase()) {
    case 'MERGED':
      return {Icon: PullRequestStateIcons.MERGED, label: 'Merged'}
    case 'CLOSED':
      return {Icon: PullRequestStateIcons.CLOSED, label: 'Closed'}
    case 'OPEN':
      return pullRequest.isDraft
        ? {Icon: PullRequestStateIcons.DRAFT, label: 'Draft'}
        : {Icon: PullRequestStateIcons.OPEN, label: 'Open'}
    default:
      return pullRequest.isDraft
        ? {Icon: PullRequestStateIcons.DRAFT, label: 'Draft'}
        : {Icon: GitPullRequestIcon, label: 'State unavailable'}
  }
}

function getLinkedPullRequestReference(pullRequest: IssueLinkedPullRequestSummary) {
  if (pullRequest.number == null) return pullRequest.repoNameWithOwner ?? 'Pull request'
  return pullRequest.repoNameWithOwner
    ? `${pullRequest.repoNameWithOwner}#${pullRequest.number}`
    : `#${pullRequest.number}`
}

function activateLinkOnSpace(event: KeyboardEvent<HTMLAnchorElement>) {
  // Primer ActionList.LinkItem handles Enter but currently closes without following
  // the link on Space, so match the documented ActionMenu keyboard behavior here.
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  if (event.key !== ' ') return
  event.currentTarget.click()
}

const LinkedPullRequestMenuItem = memo(function LinkedPullRequestMenuItemComponent({
  pullRequest,
}: {
  pullRequest: IssueLinkedPullRequestSummary
}) {
  const {Icon, label} = getLinkedPullRequestVisual(pullRequest)
  const reference = getLinkedPullRequestReference(pullRequest)

  return pullRequest.permalink ? (
    <ActionList.LinkItem
      href={pullRequest.permalink}
      target="_blank"
      rel="noreferrer"
      {...{onKeyDown: activateLinkOnSpace}}
    >
      <ActionList.LeadingVisual>
        <Icon aria-hidden="true" />
      </ActionList.LeadingVisual>
      {pullRequest.title || reference}
      <ActionList.Description variant="block">
        {reference} · {label}
      </ActionList.Description>
    </ActionList.LinkItem>
  ) : (
    <ActionList.Item inactiveText="Pull request link unavailable">
      <ActionList.LeadingVisual>
        <Icon aria-hidden="true" />
      </ActionList.LeadingVisual>
      {pullRequest.title || reference}
      <ActionList.Description variant="block">
        {reference} · {label}
      </ActionList.Description>
    </ActionList.Item>
  )
})

function LinkedPullRequestMetadata({
  totalCount,
  pullRequests,
}: {
  totalCount: number | undefined
  pullRequests: IssueLinkedPullRequestSummary[] | undefined
}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  if (!totalCount || totalCount < 1) return null

  const summaries = pullRequests ?? []
  const singlePullRequest = totalCount === 1 && summaries.length === 1 ? summaries[0] : undefined

  if (singlePullRequest?.permalink && singlePullRequest.number != null) {
    const reference = getLinkedPullRequestReference(singlePullRequest)
    const {Icon, label: stateLabel} = getLinkedPullRequestVisual(singlePullRequest)
    const label = singlePullRequest.title
      ? `1 linked pull request ${reference}: ${singlePullRequest.title}, ${stateLabel}`
      : `1 linked pull request ${reference}, ${stateLabel}`

    return (
      <ListItemMetadata>
        <Link
          href={singlePullRequest.permalink}
          target="_blank"
          rel="noreferrer"
          aria-label={label}
          className="d-inline-flex flex-items-center gap-1"
          onClick={event => event.stopPropagation()}
          {...getHovercardAttributesForType('pull_request', {
            url: `${singlePullRequest.permalink.replace(/\/$/, '')}/hovercard`,
          })}
        >
          <Icon aria-hidden="true" size={16} />
          <span aria-hidden="true">1</span>
        </Link>
      </ListItemMetadata>
    )
  }

  if (summaries.length === 0 || totalCount === 1) {
    return (
      <ListItemMetadata>
        <span
          role="img"
          aria-label={`${totalCount} linked pull ${totalCount === 1 ? 'request' : 'requests'}`}
          className="d-inline-flex flex-items-center gap-1"
        >
          <GitPullRequestIcon aria-hidden="true" size={16} />
          <span aria-hidden="true">{totalCount}</span>
        </span>
      </ListItemMetadata>
    )
  }

  const hasMorePullRequests = totalCount > summaries.length
  const triggerLabel = hasMorePullRequests
    ? `${totalCount} linked pull requests, showing first ${summaries.length}`
    : `${totalCount} linked pull requests`

  return (
    <ListItemMetadata>
      <ActionMenu>
        <ActionMenu.Anchor>
          <Button
            type="button"
            variant="link"
            size="small"
            leadingVisual={GitPullRequestIcon}
            aria-label={triggerLabel}
            onClick={event => event.stopPropagation()}
          >
            {totalCount}
          </Button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align="end" onClick={event => event.stopPropagation()}>
          <ActionList>
            {summaries.map((pullRequest, index) => (
              <LinkedPullRequestMenuItem
                // The backend order is canonical and capped, so this index is stable for a response.
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`${pullRequest.permalink ?? 'missing'}-${index}`}
                pullRequest={pullRequest}
              />
            ))}
          </ActionList>
          {hasMorePullRequests && (
            <div className="d-flex flex-items-center gap-1 px-3 pb-2 color-fg-muted">
              <InfoIcon aria-hidden="true" size={16} />
              Only the first {summaries.length} of {totalCount} linked pull requests are shown.
            </div>
          )}
        </ActionMenu.Overlay>
      </ActionMenu>
    </ListItemMetadata>
  )
}

interface IssueListItemProps {
  issue: EnrichedIssue
  onFilterSelect?: (type: FilterSelectType, value: string) => void
  /** Builds the query-preserving URL to filter the list by an issue type name. */
  getIssueTypeHref: (issueTypeName: string) => string
  /**
   * Optional client-router link component for the issue type token (SPA nav).
   * When omitted the token renders a plain anchor from `getIssueTypeHref`.
   */
  issueTypeLinkComponent?: ElementType
  /**
   * Current sort key. When it is `updated` the row appends a "· Updated <time>"
   * suffix, matching the legacy row's behavior under the recently-updated sort.
   */
  sortingItemSelected?: string
}

export function IssueListItem({
  issue,
  onFilterSelect,
  getIssueTypeHref,
  issueTypeLinkComponent,
  sortingItemSelected,
}: IssueListItemProps) {
  const {variant} = useListViewVariant()
  const isCompact = variant === 'compact'
  const stateIcon = getIssueStateIcon(issue.state, issue.stateReason)
  const issueType = issue.issueType
  const milestone = issue.milestone
  // `isReadByCurrentUser` arrives with the deferred payload; treat rows as read
  // until it loads so the unread accent doesn't flash before enrichment.
  const isRead = issue.isReadByCurrentUser ?? true

  // Mirror the legacy issue row phrasing: open issues read "<author> opened
  // <createdAt>", closed issues read "by <author> was closed <closedAt>".
  const activityTimestamp = issue.closed ? (issue.closedAt ?? issue.createdAt) : issue.createdAt
  // `getSortFromQuery` returns the full sort value including direction (e.g.
  // `updated-desc`), so strip the suffix before comparing to the `updated` field.
  const isUpdatedSort = sortingItemSelected?.replace(/-(asc|desc)$/, '') === 'updated'
  // Copilot-authored issues display "Copilot" rather than the bot login, matching the legacy row.
  const authorLabel = issue.author.isCopilot ? 'Copilot' : issue.author.login

  const labelBadges = isCompact
    ? undefined
    : renderLabelBadges({
        labels: issue.labels,
        onSelect: onFilterSelect ? label => onFilterSelect('label', label) : undefined,
        filterLinkClassName: styles.filterLink,
      })

  // Legacy renders the sub-issues progress as a title trailing badge (next to
  // the labels), not as description text.
  const subIssues = issue.subIssuesSummary
  const subIssuesBadge =
    subIssues && subIssues.total > 0 ? (
      <ListItemTrailingBadge key="sub-issues-summary" title="sub-issues summary">
        <span data-testid="sub-issues-summary">
          <Token
            leadingVisual={() => (
              <ProgressCircle
                percentCompleted={subIssues.percentCompleted}
                size={14}
                svgClassName={styles.subIssuesProgressCircle}
              />
            )}
            text={`${subIssues.completed} / ${subIssues.total}`}
          />
        </span>
      </ListItemTrailingBadge>
    ) : null

  const trailingBadges = subIssuesBadge ? [subIssuesBadge, ...(labelBadges ?? [])] : labelBadges

  return (
    <ListItem
      key={issue.id}
      className={unreadRowClassName(isRead)}
      title={
        <ListItemTitle
          href={issue.permalink}
          value={issue.titleHtml ? <UnsafeHTMLText as="span" html={issue.titleHtml} /> : <span>{issue.title}</span>}
          linkProps={{'data-testid': 'issue-listitem-title-link'}}
          anchorClassName={inlineCodeStyles.inlineCodeTitle}
          trailingBadges={trailingBadges}
        />
      }
      metadata={
        <>
          <LinkedPullRequestMetadata
            totalCount={issue.linkedPullRequestCount}
            pullRequests={issue.linkedPullRequests}
          />
          {issue.commentCount > 0 ? (
            <ListItemMetadata>
              <CommentIcon size={16} aria-label={`${issue.commentCount} comments`} /> {issue.commentCount}
            </ListItemMetadata>
          ) : null}
          {issue.assignees.length > 0 && (
            <ListItemMetadata alignment="right">
              <AssigneeAvatars
                assignees={issue.assignees}
                onSelect={onFilterSelect ? login => onFilterSelect('assignee', login) : undefined}
                filterLinkClassName={styles.assigneeFilterButton}
                alignRight
              />
            </ListItemMetadata>
          )}
        </>
      }
    >
      <ListItemLeadingContent>
        <UnreadRowLabel isRead={isRead} />
        <ListItemLeadingVisual>{stateIcon}</ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        <ListItemDescription>
          {issueType && (
            <IssueTypeToken
              name={issueType.name}
              color={issueType.color}
              getTooltipText={isTruncated => (isTruncated ? issueType.name : undefined)}
              {...(issueTypeLinkComponent
                ? {as: issueTypeLinkComponent, to: getIssueTypeHref(issueType.name)}
                : {href: getIssueTypeHref(issueType.name)})}
            />
          )}
          {!issue.closed && issue.blockedByCount != null && issue.blockedByCount > 0 && (
            <Token
              data-testid="blocked-by"
              text="Blocked"
              leadingVisual={() => <BlockedIcon className="fgColor-danger" size={14} />}
              className={styles.blockedToken}
            />
          )}
          <span>
            {issue.repoNameWithOwner}#{issue.number}
          </span>
          {issue.author && (
            <span>
              {' · '}
              {issue.closed && <span>by </span>}
              {authorLabel} <span>{issue.closed ? 'was closed' : 'opened'}</span>{' '}
              <IssueRelativeTime datetime={activityTimestamp} />
            </span>
          )}
          {isUpdatedSort && (
            <span data-testid="updated-at">
              {' · '}Updated <IssueRelativeTime datetime={issue.updatedAt} />
            </span>
          )}
          {/* Legacy renders a text label only for duplicates, since the duplicate and
              not-planned state icons share the same muted skip glyph. */}
          {issue.stateReason === 'DUPLICATE' && <span data-testid="state-reason"> · Duplicate</span>}
          {milestone && (
            <span>
              {' · '}
              <Link href={milestone.url} muted aria-label={`Milestone: ${milestone.title}`}>
                <MilestoneIcon size={16} /> {milestone.title}
              </Link>
            </span>
          )}
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}
